import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { travelRequestsApi } from '../../api/travel';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CommentsThread } from '../../components/CommentsThread';
import { useToast } from '../../components/Toast';
import { TRAVEL_STATUS_LABELS } from './TravelRequestsListPage';
import { TRAVEL_REQUEST_STAGES } from '../../config/workflow-stages';
import { formatDate, formatDateTime } from '../../utils/formatDate';

export function TravelRequestWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const travelRequestId = Number(id);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: requestData } = useQuery({ queryKey: ['travel-request', travelRequestId], queryFn: () => travelRequestsApi.get(travelRequestId) });
  const travelRequest = requestData?.data;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['travel-request', travelRequestId] });

  const submitMutation = useMutation({
    mutationFn: () => travelRequestsApi.submit(travelRequestId),
    onSuccess: () => { showSuccess('Travel request submitted.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const { register: registerDecision, handleSubmit: handleDecisionSubmit, reset: resetDecision } = useForm<{ comment: string }>();
  const decideMutation = useMutation({
    mutationFn: ({ decision, comment }: { decision: 'approved' | 'rejected'; comment: string }) => travelRequestsApi.decide(travelRequestId, decision, comment),
    onSuccess: (result) => {
      showSuccess(result.data.taReference ? `Approved. Travel Authorization ${result.data.taReference} issued.` : `Decision recorded (${result.data.status}).`);
      resetDecision();
      invalidate();
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: (reason: string) => travelRequestsApi.cancel(travelRequestId, reason),
    onSuccess: () => { showSuccess('Travel request cancelled.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const retryTaMutation = useMutation({
    mutationFn: () => travelRequestsApi.retryIssueTa(travelRequestId),
    onSuccess: (result) => { showSuccess(`Travel Authorization ${result.data.taReference} issued.`); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const createVehicleRequestMutation = useMutation({
    mutationFn: () => travelRequestsApi.createVehicleRequest(travelRequestId),
    onSuccess: (result) => showSuccess(`Linked Vehicle Request ${result.data.vehicleRequestReference} created.`),
    onError: (err: ApiError) => showError(err.message),
  });

  if (!travelRequest) return <div className="page"><p className="muted">Loading…</p></div>;

  return (
    <div className="page">
      <PageHeader
        title={travelRequest.request_reference}
        description={`${travelRequest.origin} → ${travelRequest.destination} · ${formatDate(travelRequest.departure_date)} to ${formatDate(travelRequest.return_date)}`}
      />
      <p><StatusBadge status={travelRequest.status} label={TRAVEL_STATUS_LABELS[travelRequest.status]} /></p>

      <WorkspaceTabs
        tabs={[
          {
            key: 'overview', label: 'Overview', content: (
              <div>
                <SectionHeader title="Details" />
                <dl className="detail-grid">
                  <dt>Purpose</dt><dd>{travelRequest.purpose}</dd>
                  <dt>Business Justification</dt><dd>{travelRequest.business_justification ?? '-'}</dd>
                  <dt>Mode of Travel</dt><dd>{travelRequest.mode_of_travel ?? 'Not yet decided'}</dd>
                  <dt>Estimated Cost</dt><dd>{travelRequest.estimated_cost ? `${travelRequest.estimated_cost_currency} ${travelRequest.estimated_cost}` : '-'}</dd>
                </dl>

                {travelRequest.status === 'draft' && (
                  <button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>Submit for Approval</button>
                )}
                {!['rejected', 'cancelled', 'closed', 'archived'].includes(travelRequest.status) && (
                  <form className="inline-form" onSubmit={(e) => { e.preventDefault(); const reason = (e.target as HTMLFormElement).reason.value; if (reason) cancelMutation.mutate(reason); }}>
                    <input name="reason" placeholder="Reason for cancellation" />
                    <button type="submit" disabled={cancelMutation.isPending}>Cancel Travel Request</button>
                  </form>
                )}
                {travelRequest.mode_of_travel === 'road_organization_vehicle' && travelRequest.status === 'approved' && (
                  <button onClick={() => createVehicleRequestMutation.mutate()} disabled={createVehicleRequestMutation.isPending}>Create Linked Vehicle Request</button>
                )}
              </div>
            ),
          },
          { key: 'itinerary', label: 'Itinerary', content: <ItineraryTab travelRequestId={travelRequestId} /> },
          {
            key: 'approval', label: 'Approval & Authorization', content: (
              <div>
                <SectionHeader title="Approval Chain" />
                <p className="muted">Initiator → Supervisor → Logistics → Logistics Manager → Head of Operations (final)</p>

                {['submitted', 'under_review'].includes(travelRequest.status) && (
                  <form className="inline-form" onSubmit={handleDecisionSubmit((d) => decideMutation.mutate({ decision: 'approved', comment: d.comment }))}>
                    <textarea placeholder="Comment (required)" {...registerDecision('comment', { required: true })} />
                    <button type="submit" disabled={decideMutation.isPending}>Approve</button>
                    <button type="button" onClick={handleDecisionSubmit((d) => decideMutation.mutate({ decision: 'rejected', comment: d.comment }))} disabled={decideMutation.isPending}>Reject</button>
                  </form>
                )}

                <SectionHeader title="Travel Authorization" nested />
                <TravelAuthorizationSection travelRequestId={travelRequestId} status={travelRequest.status} onRetryIssueTa={() => retryTaMutation.mutate()} retrying={retryTaMutation.isPending} />
              </div>
            ),
          },
          { key: 'arrangements', label: 'Arrangements & Accommodation', content: <ArrangementsAccommodationTab travelRequestId={travelRequestId} /> },
          { key: 'advance', label: 'Advance', content: <AdvanceTab travelRequestId={travelRequestId} /> },
          { key: 'report', label: 'Mission Report', content: <MissionReportTab travelRequestId={travelRequestId} /> },
          { key: 'expenses', label: 'Expenses & Reconciliation', content: <ExpensesReconciliationTab travelRequestId={travelRequestId} /> },
          {
            key: 'tracking', label: 'Tracking', content: (
              <div>
                <WorkflowTrackingTab entityType="travel_request" entityId={travelRequestId} actionStageMap={TRAVEL_REQUEST_STAGES} statusLabels={TRAVEL_STATUS_LABELS} />
                <CommentsThread entityType="travel_request" entityId={travelRequestId} section="travel" heading="Comments for this Travel Request" />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

function ItineraryTab({ travelRequestId }: { travelRequestId: number }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { data } = useQuery({ queryKey: ['travel-legs', travelRequestId], queryFn: () => travelRequestsApi.listLegs(travelRequestId) });
  const { register, handleSubmit, reset } = useForm<{ sequence: number; departureLocation: string; destinationLocation: string; departureDatetime: string; transportMode: string }>();

  const addLegMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => travelRequestsApi.addLeg(travelRequestId, input),
    onSuccess: () => { showSuccess('Itinerary leg added.'); reset(); queryClient.invalidateQueries({ queryKey: ['travel-legs', travelRequestId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  const legs = data?.data ?? [];

  return (
    <div>
      <SectionHeader title="Itinerary Legs" />
      {legs.length === 0 ? <p className="muted">No itinerary legs added yet.</p> : (
        <ol>
          {legs.map((leg) => (
            <li key={leg.id}>{leg.departure_location} → {leg.destination_location} ({leg.transport_mode}, departs {formatDateTime(leg.departure_datetime)})</li>
          ))}
        </ol>
      )}
      <form className="inline-form" onSubmit={handleSubmit((d) => addLegMutation.mutate({ ...d, sequence: Number(d.sequence) }))}>
        <input type="number" placeholder="Sequence" {...register('sequence', { required: true })} />
        <input placeholder="Departure location" {...register('departureLocation', { required: true })} />
        <input placeholder="Destination location" {...register('destinationLocation', { required: true })} />
        <input type="datetime-local" {...register('departureDatetime', { required: true })} />
        <select {...register('transportMode', { required: true })}>
          <option value="air">Air</option><option value="road">Road</option><option value="rail">Rail</option><option value="other">Other</option>
        </select>
        <button type="submit" disabled={addLegMutation.isPending}>Add Leg</button>
      </form>
    </div>
  );
}

function TravelAuthorizationSection({ travelRequestId, status, onRetryIssueTa, retrying }: { travelRequestId: number; status: string; onRetryIssueTa: () => void; retrying: boolean }) {
  const { data, isError } = useQuery({ queryKey: ['travel-authorization', travelRequestId], queryFn: () => travelRequestsApi.getAuthorization(travelRequestId), retry: false });

  if (data?.data) {
    return (
      <div className="ta-document">
        <h3>TRAVEL AUTHORIZATION</h3>
        <p><strong>TA Reference:</strong> {data.data.ta_reference}</p>
        <p><strong>Issued:</strong> {formatDateTime(data.data.issued_at)}</p>
        <p className="muted">Full UN-format document layout to be rendered here.</p>
      </div>
    );
  }

  if (isError && status === 'approved') {
    return (
      <div>
        <p className="muted">No Travel Authorization has been issued yet - this usually means the linked activity's Terms of Reference has not been approved.</p>
        <button onClick={onRetryIssueTa} disabled={retrying}>Retry Issue TA</button>
      </div>
    );
  }

  return <p className="muted">A Travel Authorization is issued automatically once the request is fully approved and the linked activity's ToR is approved.</p>;
}

function ArrangementsAccommodationTab({ travelRequestId }: { travelRequestId: number }) {
  const { data: arrangements } = useQuery({ queryKey: ['travel-arrangements', travelRequestId], queryFn: () => travelRequestsApi.listArrangements(travelRequestId) });
  const { data: accommodation } = useQuery({ queryKey: ['travel-accommodation', travelRequestId], queryFn: () => travelRequestsApi.listAccommodation(travelRequestId) });

  return (
    <div>
      <SectionHeader title="Arrangements" />
      {(arrangements?.data ?? []).length === 0 ? <p className="muted">No arrangements recorded yet.</p> : (
        <ul>{(arrangements?.data ?? []).map((a) => <li key={a.id as number}>{a.arrangement_type as string} - {a.provider as string ?? 'No provider'} ({a.status as string})</li>)}</ul>
      )}
      <SectionHeader title="Accommodation" nested />
      {(accommodation?.data ?? []).length === 0 ? <p className="muted">No accommodation booked yet.</p> : (
        <ul>{(accommodation?.data ?? []).map((a) => <li key={a.id as number}>{a.provider as string}, {a.city_location as string} ({a.check_in_date as string} to {a.check_out_date as string})</li>)}</ul>
      )}
    </div>
  );
}

function AdvanceTab({ travelRequestId }: { travelRequestId: number }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { data, isError } = useQuery({ queryKey: ['travel-advance', travelRequestId], queryFn: () => travelRequestsApi.getAdvance(travelRequestId), retry: false });
  const { register, handleSubmit } = useForm<{ requestedAmount: number }>();

  const requestMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => travelRequestsApi.requestAdvance(travelRequestId, input),
    onSuccess: () => { showSuccess('Advance requested.'); queryClient.invalidateQueries({ queryKey: ['travel-advance', travelRequestId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  if (data?.data) {
    const advance = data.data;
    return (
      <div>
        <SectionHeader title="Travel Advance" />
        <p><strong>Reference:</strong> {advance.advance_reference as string}</p>
        <p><StatusBadge status={advance.status as string} label={(advance.status as string).charAt(0).toUpperCase() + (advance.status as string).slice(1)} /></p>
        <p><strong>Requested:</strong> {advance.currency as string} {advance.requested_amount as string}</p>
        {advance.approved_amount ? <p><strong>Approved:</strong> {advance.currency as string} {advance.approved_amount as string}</p> : null}
      </div>
    );
  }

  return (
    <div>
      <SectionHeader title="Travel Advance" />
      {isError && <p className="muted">No advance has been requested for this travel yet.</p>}
      <form className="inline-form" onSubmit={handleSubmit((d) => requestMutation.mutate({ requestedAmount: Number(d.requestedAmount) }))}>
        <input type="number" step="0.01" placeholder="Requested amount" {...register('requestedAmount', { required: true })} />
        <button type="submit" disabled={requestMutation.isPending}>Request Advance</button>
      </form>
    </div>
  );
}

function MissionReportTab({ travelRequestId }: { travelRequestId: number }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { data, isError } = useQuery({ queryKey: ['travel-report', travelRequestId], queryFn: () => travelRequestsApi.getReport(travelRequestId), retry: false });
  const { register, handleSubmit } = useForm<{ locationsVisited: string; activitiesUndertaken: string; submissionDate: string }>();

  const submitMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => travelRequestsApi.submitReport(travelRequestId, input),
    onSuccess: () => { showSuccess('Mission report submitted.'); queryClient.invalidateQueries({ queryKey: ['travel-report', travelRequestId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  if (data?.data) {
    const report = data.data;
    return (
      <div>
        <SectionHeader title="Mission Report" />
        <dl className="detail-grid">
          <dt>Locations Visited</dt><dd>{report.locations_visited as string}</dd>
          <dt>Activities Undertaken</dt><dd>{report.activities_undertaken as string}</dd>
          <dt>Review Status</dt><dd><StatusBadge status={report.supervisor_review_status as string} label={report.supervisor_review_status as string} /></dd>
        </dl>
      </div>
    );
  }

  return (
    <div>
      <SectionHeader title="Mission Report" />
      {isError && <p className="muted">No mission report has been submitted yet.</p>}
      <form className="inline-form" onSubmit={handleSubmit((d) => submitMutation.mutate(d))}>
        <textarea placeholder="Locations visited" {...register('locationsVisited', { required: true })} />
        <textarea placeholder="Activities undertaken" {...register('activitiesUndertaken', { required: true })} />
        <input type="date" {...register('submissionDate', { required: true })} />
        <button type="submit" disabled={submitMutation.isPending}>Submit Report</button>
      </form>
    </div>
  );
}

function ExpensesReconciliationTab({ travelRequestId }: { travelRequestId: number }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { data: expensesData } = useQuery({ queryKey: ['travel-expenses', travelRequestId], queryFn: () => travelRequestsApi.listExpenses(travelRequestId) });
  const { register, handleSubmit, reset } = useForm<{ expenseDate: string; category: string; description: string; amount: number }>();

  const addExpenseMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => travelRequestsApi.addExpense(travelRequestId, input),
    onSuccess: () => { showSuccess('Expense added.'); reset(); queryClient.invalidateQueries({ queryKey: ['travel-expenses', travelRequestId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  const calculateMutation = useMutation({
    mutationFn: () => travelRequestsApi.calculateReconciliation(travelRequestId),
    onSuccess: () => showSuccess('Reconciliation calculated.'),
    onError: (err: ApiError) => showError(err.message),
  });

  const expenses = expensesData?.data ?? [];
  const reconciliation = calculateMutation.data?.data;

  return (
    <div>
      <SectionHeader title="Expense Claims" />
      {expenses.length === 0 ? <p className="muted">No expenses claimed yet.</p> : (
        <ul>{expenses.map((e) => <li key={e.id as number}>{e.expense_date as string} - {e.category as string} - {e.currency as string} {e.amount as string}</li>)}</ul>
      )}
      <form className="inline-form" onSubmit={handleSubmit((d) => addExpenseMutation.mutate({ ...d, amount: Number(d.amount) }))}>
        <input type="date" {...register('expenseDate', { required: true })} />
        <select {...register('category', { required: true })}>
          <option value="airfare">Airfare</option><option value="road_transport">Road Transport</option><option value="local_transport">Local Transport</option>
          <option value="accommodation">Accommodation</option><option value="dsa_per_diem">DSA/Per Diem</option><option value="visa">Visa</option>
          <option value="airport_transfer">Airport Transfer</option><option value="fuel">Fuel</option><option value="communication">Communication</option>
          <option value="conference_registration">Conference Registration</option><option value="security_related">Security-Related</option><option value="miscellaneous">Miscellaneous</option>
        </select>
        <input placeholder="Description" {...register('description', { required: true })} />
        <input type="number" step="0.01" placeholder="Amount" {...register('amount', { required: true })} />
        <button type="submit" disabled={addExpenseMutation.isPending}>Add Expense</button>
      </form>

      <SectionHeader title="Reconciliation" nested />
      <button onClick={() => calculateMutation.mutate()} disabled={calculateMutation.isPending}>Calculate Reconciliation</button>
      {reconciliation && (
        <dl className="detail-grid">
          <dt>Advance Received</dt><dd>{reconciliation.currency} {reconciliation.advanceReceived}</dd>
          <dt>Eligible Expenses</dt><dd>{reconciliation.currency} {reconciliation.eligibleExpensesTotal}</dd>
          <dt>Balance</dt><dd>{reconciliation.currency} {reconciliation.balanceAmount} ({reconciliation.balanceDirection.replace(/_/g, ' ')})</dd>
        </dl>
      )}
    </div>
  );
}
